import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Notify, Loading } from 'notiflix';
import {
  Box,
  Card,
  Grid,
  TextField,
  Button,
  Typography,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import { Save, ArrowBack, Edit, Delete, Add, Visibility } from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import {
  fetchCommonValuePackData,
  updateCommonValuePackData,
  selectCommonData,
  selectLoading,
  selectSubmitting
} from 'store/slices/valuePackSlice';
import {
  fetchIndustries,
  deleteIndustry,
  selectIndustries,
  selectLoading as selectIndustryLoading,
  selectPagination
} from 'store/slices/industrySlice';

const ValuePackCommonSettings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Value Pack Common Data
  const commonData = useSelector(selectCommonData);
  const loading = useSelector(selectLoading);
  const submitting = useSelector(selectSubmitting);
  
  // Industry Data
  const industries = useSelector(selectIndustries);
  const industryLoading = useSelector(selectIndustryLoading);
  const pagination = useSelector(selectPagination);
  
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    heroTitle: '',
    heroSubtitle: '',
    defaultDescription: '',
    termsAndConditions: '',
    refundPolicy: '',
    supportEmail: '',
    supportPhone: '',
  });

  // Pagination for industries table
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(fetchCommonValuePackData()).unwrap();
        await dispatch(fetchIndustries({
          page: page + 1,
          limit: rowsPerPage,
        })).unwrap();
      } catch (error) {
        Notify.failure(error || 'Failed to load data');
      }
    };
    
    loadData();
  }, [dispatch, page, rowsPerPage]);

  useEffect(() => {
    if (commonData) {
      setFormData(commonData);
    }
  }, [commonData]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      Loading.circle('Saving common settings...');
      await dispatch(updateCommonValuePackData(formData)).unwrap();
      Notify.success('Common settings saved successfully');
    } catch (error) {
      Notify.failure(error || 'Failed to save common settings');
    } finally {
      Loading.remove();
    }
  };

  // Industry Functions
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditIndustry = (industry) => {
    navigate(`/industries/edit/${industry.id}`);
  };

  const handleDeleteIndustry = async (industry) => {
    const result = window.confirm(`Are you sure you want to delete "${industry.name}"?`);
    
    if (result) {
      try {
        await dispatch(deleteIndustry(industry.id)).unwrap();
        Notify.success('Industry deleted successfully');
        // Reload industries
        await dispatch(fetchIndustries({
          page: page + 1,
          limit: rowsPerPage,
        })).unwrap();
      } catch (error) {
        Notify.failure(error || 'Failed to delete industry');
      }
    }
  };

  const handleCreateIndustry = () => {
    navigate('/industries/create');
  };

  const handleViewFeatures = (industry) => {
    navigate(`/industries/${industry.id}/features`);
  };

  // Tab Panel Component
  const TabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box pt={6} pb={3} display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
          <Typography variant="h6" ml={2}>Loading common settings...</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Box mb={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/value-pack')}
            sx={{ mb: 2 }}
          >
            Back to Value Packs
          </Button>
        </Box>

        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              Value Pack Common Settings
            </MDTypography>
          </MDBox>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="common settings tabs">
              <Tab label="General Settings" />
              <Tab label="Industry Management" />
            </Tabs>
          </Box>

          {/* General Settings Tab */}
          <TabPanel value={activeTab} index={0}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MDTypography variant="h6" gutterBottom>
                    Hero Section
                  </MDTypography>
                  <Divider />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Hero Title"
                    value={formData.heroTitle}
                    onChange={handleChange('heroTitle')}
                    variant="outlined"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Hero Subtitle"
                    value={formData.heroSubtitle}
                    onChange={handleChange('heroSubtitle')}
                    variant="outlined"
                    multiline
                    rows={2}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Default Description"
                    value={formData.defaultDescription}
                    onChange={handleChange('defaultDescription')}
                    variant="outlined"
                    multiline
                    rows={4}
                  />
                </Grid>

                <Grid item xs={12}>
                  <MDTypography variant="h6" gutterBottom>
                    Policies & Support
                  </MDTypography>
                  <Divider />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Terms & Conditions"
                    value={formData.termsAndConditions}
                    onChange={handleChange('termsAndConditions')}
                    variant="outlined"
                    multiline
                    rows={6}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Refund Policy"
                    value={formData.refundPolicy}
                    onChange={handleChange('refundPolicy')}
                    variant="outlined"
                    multiline
                    rows={4}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Support Email"
                    value={formData.supportEmail}
                    onChange={handleChange('supportEmail')}
                    variant="outlined"
                    type="email"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Support Phone"
                    value={formData.supportPhone}
                    onChange={handleChange('supportPhone')}
                    variant="outlined"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={submitting ? <CircularProgress size={20} /> : <Save />}
                      disabled={submitting}
                      sx={{
                        backgroundColor: 'info.main',
                        '&:hover': {
                          backgroundColor: 'info.dark',
                        },
                      }}
                    >
                      {submitting ? 'Saving...' : 'Save Settings'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </TabPanel>

          {/* Industry Management Tab */}
          <TabPanel value={activeTab} index={1}>
            <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h6">
                Industries Management
              </MDTypography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateIndustry}
                sx={{
                  backgroundColor: 'info.main',
                  '&:hover': {
                    backgroundColor: 'info.dark',
                  },
                }}
              >
                Add Industry
              </Button>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Icon</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Order</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {industryLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <CircularProgress />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Loading industries...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : industries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No industries found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    industries?.map((industry) => (
                      <TableRow key={industry.id}>
                        <TableCell>
                          <Avatar
                            src={industry.icon}
                            alt={industry.name}
                            sx={{ width: 40, height: 40 }}
                          >
                            {industry.name.charAt(0)}
                          </Avatar>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {industry.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary" noWrap sx={{ maxWidth: 200 }}>
                            {industry.description || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={industry.order} variant="outlined" size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={industry.is_active ? 'Active' : 'Inactive'}
                            color={industry.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Tooltip title="View Features">
                              <IconButton
                                size="small"
                                onClick={() => handleViewFeatures(industry)}
                                color="info"
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditIndustry(industry)}
                                color="primary"
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteIndustry(industry)}
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={pagination.total || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            /> */}
          </TabPanel>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default ValuePackCommonSettings;
